import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { config } from '@accounts/config';
import { Factors, Passkeys, Resume, Second } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, Field, Form } from '@beyond-js/ui/react';
import { Problem, Wordmark } from '@accounts/ui';

/**
 * The second step of a sign-in for an account with a second factor (decision E8): a passkey, an
 * authenticator code or a recovery code, with a switch between them. A wrong code says how many
 * attempts are left; a ticket that expired or ran out sends the person back to sign in, keeping where
 * they were going. While the code is empty Continue waits quietly and a press puts the caret in it (D183).
 */
export function Factor() {
	const { text, client, refresh } = usePresence();
	const factors = useMemo(() => new Factors(client), [client]);
	const [parameters] = useSearchParams();
	const [step] = useState(() => Second.read());
	const methods = (step?.methods ?? []).filter(method => method !== 'passkey' || Passkeys.supported);
	const [method, setMethod] = useState(methods[0]);
	const [code, setCode] = useState('');
	const [busy, setBusy] = useState(false);
	const [failure, setFailure] = useState<unknown>();
	const [left, setLeft] = useState<number>();
	const [expired, setExpired] = useState(!step);
	const back = `/signin?${(() => {
		const kept = new URLSearchParams(parameters);
		kept.delete('hint');
		return kept;
	})()}`;

	async function prove() {
		if (!step || !method) return;
		setBusy(true);
		setFailure(undefined);
		try {
			await factors.finish(step.ticket, method, code.trim());
			Second.forget();
			const next = Resume.landed(parameters) ?? Resume.handoff(parameters.get('to'), config.authority);
			if (next) globalThis.location.href = next;
			else await refresh();
		} catch (error) {
			setCode('');
			if (Refusal.is(error) && error.code === 'TICKET_INVALID') {
				Second.forget();
				setExpired(true);
			} else if (Refusal.is(error) && error.code === 'FACTOR_INVALID') setLeft(Number(error.details?.attempts ?? 0));
			else if (!(Refusal.is(error) && error.quiet)) setFailure(error);
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4 py-10">
			<header className="flex flex-col gap-3">
				<Wordmark label="Accounts" />
				<div>
					<h1 className="text-title-1 text-ink">{text('secondstep.title')}</h1>
					{method && !expired ? <p className="mt-1 text-body text-ink-muted">{text(`secondstep.lead.${method}`)}</p> : null}
				</div>
			</header>

			{failure ? <Problem error={failure} /> : null}
			{expired ? (
				<Callout tone="warning" live title={text('factor.ticket')} actions={<Link className="underline" to={back}>{text('secondstep.restart')}</Link>} />
			) : (
				<Form
					className="flex flex-col gap-4"
					onSubmit={event => {
						event.preventDefault();
						void prove();
					}}
				>
					{left !== undefined ? <Callout tone="danger" live title={`${text('factor.invalid')} ${text('secondstep.attempts', { count: left })}`} /> : null}
					{method === 'passkey' ? (
						<Button type="submit" variant="primary" busy={busy}>
							{text('secondstep.passkey')}
						</Button>
					) : (
						<>
							<Field label={text('secondstep.code')}>
								<input autoFocus inputMode={method === 'totp' ? 'numeric' : 'text'} autoComplete="one-time-code" required value={code} onChange={event => setCode(event.target.value)} />
							</Field>
							<Button type="submit" variant="primary" busy={busy}>
								{text('secondstep.submit')}
							</Button>
						</>
					)}
					{methods
						.filter(other => other !== method)
						.map(other => (
							<Button key={other} type="button" variant="quiet" onClick={() => (setMethod(other), setLeft(undefined), setCode(''))}>
								{text(`secondstep.other.${other}`)}
							</Button>
						))}
				</Form>
			)}
		</div>
	);
}
